import type { Invoice } from "../types";

interface ProcessingStatusProps {
  invoice: Invoice;
}

/**
 * Processing status panel shown while an invoice is being processed.
 * Displays the current step name, a progress bar, and an animated spinner.
 */
export default function ProcessingStatus({ invoice }: ProcessingStatusProps) {
  const { processing_step, processing_progress, total_lines } = invoice;
  const pct =
    total_lines > 0
      ? Math.min(100, Math.round((processing_progress / total_lines) * 100))
      : 0;

  return (
    <div className="rounded-lg border border-blue-200 bg-blue-50 p-6">
      <div className="flex items-center gap-3">
        {/* Animated spinner */}
        <svg
          className="h-5 w-5 animate-spin text-blue-600"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
          />
        </svg>
        <h3 className="text-sm font-semibold text-blue-800">
          Processing Invoice
        </h3>
      </div>

      {/* Step text */}
      <p className="mt-3 text-sm text-blue-700">
        {processing_step || "Initialising..."}
      </p>

      {/* Progress bar */}
      <div className="mt-3">
        <div className="flex items-center justify-between text-xs text-blue-600">
          <span>
            {processing_progress} / {total_lines} lines
          </span>
          <span>{pct}%</span>
        </div>
        <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-blue-200">
          <div
            className="h-full rounded-full bg-blue-600 transition-all duration-500 ease-out"
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>

      <p className="mt-3 text-xs text-blue-500">
        Auto-refreshing every 10 seconds...
      </p>
    </div>
  );
}
